import { FC, useEffect, useState, useCallback } from 'react';
import { X, Calendar, MapPin, Users, MessageCircle, Phone, ChevronDown } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { searchCustomersForInteractionDropdowns, selectCustomersList, selectSearchLoading } from '../../store/slices/customersSlice';
import { createCustomerInteraction, updateCustomerInteraction } from '../../store/slices/interactionsSlice';
import { createVisit } from '../../store/slices/visitsSlice';
import { Customer } from '../../lib/types';
import { getWIBTime, getWIBTimeForDirectus } from '../../lib/timeUtils';
import { getCustomerTambakId } from '../../lib/customerUtils';
import Swal from 'sweetalert2';
import { NotesModal, ProgressStatus } from './NotesModal';
import { directusOperations } from '../../lib/directus';
import { useLanguage } from '../../contexts/LanguageContext';

interface InstantVisitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVisitCreated?: () => void; // Callback untuk navigasi setelah visit dibuat
}

// Helper functions outside of component to avoid initialization issues
const getCurrentWIBDate = () => {
  const wibTime = getWIBTime(); // Returns "YYYY-MM-DDTHH:mm:ss" format
  return wibTime.split('T')[0]; // Extract date part: "YYYY-MM-DD"
};

const getCurrentWIBDateTime = () => {
  const wibTime = getWIBTime(); // Returns "YYYY-MM-DDTHH:mm:ss" format
  return wibTime.slice(0, 16); // Extract datetime-local format: "YYYY-MM-DDTHH:mm"
};

export const InstantVisitModal: FC<InstantVisitModalProps> = ({
  isOpen,
  onClose,
  onVisitCreated
}) => {
  const dispatch = useAppDispatch();
  const { t } = useLanguage();

  // Redux selectors
  const searchResults = useAppSelector(selectCustomersList);
  const isSearching = useAppSelector(selectSearchLoading);

  // State management
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [currentWIBTime, setCurrentWIBTime] = useState(getCurrentWIBDateTime());

  // Form data state with WIB real time
  const [formData, setFormData] = useState({
    customerName: '',
    customerId: '',
    customerInitial: '',
    status: '',
    date: getCurrentWIBDate(),
    type: '',
    scheduledDateTime: getCurrentWIBDateTime(),
    notes: '',
    phoneNumber: '',
    whatsappNumber: ''
  });

  // Visit creation state
  const [isCreatingVisit, setIsCreatingVisit] = useState(false);

  // NotesModal state for WhatsApp/Call
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesModalInteraction, setNotesModalInteraction] = useState<any>(null);

  // Helper function to display customer name with ID
  const displayCustomerName = (customer: Customer): string => {
    const cleanName = customer.name?.replace(/\s*\([^)]+\)$/, '') || customer.NamaPelangganTambak || t('status.unknown_customer');
    const customerId = customer.idcust || customer.id;
    return `${cleanName} (${customerId})`;
  };

  // Handle WhatsApp/Call button click - open NotesModal
  const handleWhatsAppCallClick = useCallback(async () => {
    // Check if customer is selected
    if (!selectedCustomer) {
      await Swal.fire({
        icon: 'warning',
        title: t('instant_visit_modal.customer_not_selected'),
        text: t('instant_visit_modal.please_select_customer'),
        confirmButtonText: t('common.buttons.ok'),
        confirmButtonColor: 'hsl(var(--primary))'
      });
      return;
    }

    // Create a temporary interaction object for NotesModal
    const tempInteraction = {
      id: 'temp-' + Date.now(), // Temporary ID
      customer_name: selectedCustomer.name || selectedCustomer.NamaPelangganTambak || 'Unknown Customer',
      customer_tambak_id: getCustomerTambakId(selectedCustomer),
      keterangan: '',
      interaction_type: '',
      done: false,
      file: [],
      Progress: null // Add Progress field
    };

    setNotesModalInteraction(tempInteraction);
    setShowNotesModal(true);
  }, [selectedCustomer]);
    
  // Customer search function
  const performCustomerSearch = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      return;
    }

    try {
      await dispatch(searchCustomersForInteractionDropdowns({
        query: query.trim(),
        page: 1,
        limit: 20,
        append: false
      }));
    } catch (error) {
      console.error('Customer search failed:', error);
    }
  }, [dispatch]);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (customerSearch) {
        performCustomerSearch(customerSearch);
        setShowCustomerDropdown(true);
      } else {
        setShowCustomerDropdown(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [customerSearch, performCustomerSearch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.customer-search-container')) {
        setShowCustomerDropdown(false);
      }
    };

    if (showCustomerDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCustomerDropdown]);

  // Handle customer selection - auto-populate quick contact with customer phone data
  const handleCustomerSelect = useCallback((customer: Customer) => {
    // Debug: Check all possible phone fields
    console.log('🔍 DEBUG Customer Selection:', {
      customer,
      phone: customer.phone,
      Contacs: customer.Contacs,
      Phone: customer.Phone,
      Kontak: customer.Kontak,
      allPhoneFields: {
        phone: customer.phone,
        Contacs: customer.Contacs,
        Phone: customer.Phone,
        Kontak: customer.Kontak
      }
    });

    const customerPhoneNumber = customer.phone || customer.Contacs || customer.Phone || customer.Kontak || null;
    console.log('📞 Final phone number:', customerPhoneNumber);

    setSelectedCustomer(customer);
    setCustomerSearch(displayCustomerName(customer));
    setFormData(prev => ({
      ...prev,
      customerName: customer.name || customer.NamaPelangganTambak || '',
      customerId: customer.idcust || customer.id?.toString() || '',
      customerInitial: (customer.name || customer.NamaPelangganTambak || 'U').charAt(0).toUpperCase(),
      // Auto-populate quick contact fields with customer's phone number or null if not available
      phoneNumber: customerPhoneNumber || '',
      whatsappNumber: customerPhoneNumber || ''
    }));
    setShowCustomerDropdown(false);
  }, []);

  // Handle Visit button click - create interaction and visit simultaneously
  const handleVisitClick = useCallback(async () => {
    if (!selectedCustomer || !formData.customerName) {
      alert(t('instant_visit_modal.please_select_customer_first'));
      return;
    }

    if (!formData.type) {
      alert(t('instant_visit_modal.select_type'));
      return;
    }

    setIsCreatingVisit(true);

    try {
      // Step 1: Create Customer Interaction
      const interactionData = {
        No_pelanggan: selectedCustomer.idcust || selectedCustomer.id?.toString() || '',
        keterangan: formData.notes || `Instant visit with ${formData.customerName}`,
        interaction_type: formData.type === 'visit' ? 'Visit/Meeting' :
                         formData.type === 'call' ? 'Phone Call' : 'WhatsApp',
        priority: 'medium' as const,
        date_created: getWIBTime(),
        // ✅ FIXED: Only set for Customer_Tambak records, null for Data_Customer
        customer_tambak_id: getCustomerTambakId(selectedCustomer)
      };

      console.log('Creating interaction:', interactionData);
      const interactionResult = await dispatch(createCustomerInteraction(interactionData)).unwrap();

      // Step 2: Get current location for visit
      const getCurrentLocation = (): Promise<{ latitude: number; longitude: number }> => {
        return new Promise((resolve, reject) => {
          if (!navigator.geolocation) {
            reject(new Error('Geolocation is not supported'));
            return;
          }

          navigator.geolocation.getCurrentPosition(
            (position) => {
              resolve({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude
              });
            },
            (_error) => {
              console.warn('Location access denied, using default coordinates');
              resolve({
                latitude: -6.200000, // Default Jakarta coordinates
                longitude: 106.816666
              });
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
          );
        });
      };

      const location = await getCurrentLocation();

      // Step 3: Create Visit with interaction reference
      const visitData = {
        customer_name: formData.customerName,
        customer_address: selectedCustomer.address || selectedCustomer.Alamat || '',
        latitude: location.latitude,
        longitude: location.longitude,
        notes: formData.notes || '',
        // ✅ FIXED: This should be idcust (string) not customer_tambak_id (numeric foreign key)
        customer_tambak_id: selectedCustomer.idcust || selectedCustomer.id?.toString(),
        coordinates: { lat: location.latitude, lng: location.longitude },
        start_time: getWIBTime(),
        visit_purpose: `Instant visit - ${formData.type}`,
        visit_type: formData.type,
        location_accuracy: 10,
        // Link visit to interaction for auto-completion when visit ends
        original_interaction_id: parseInt(interactionResult.id),
        isFromInteraction: true
      };

      console.log('Creating visit:', visitData);
      const visitResult = await dispatch(createVisit(visitData)).unwrap();

      // Step 4: Update interaction with visit_id (avoid double creation)
      if (interactionResult.id && visitResult.id) {
        await dispatch(updateCustomerInteraction({
          id: parseInt(interactionResult.id),
          data: {
            visit_id: parseInt(visitResult.id)
          }
        }));
      }

      console.log('✅ Instant visit created successfully!');

      // Show simple SweetAlert without buttons - auto close
      Swal.fire({
        icon: 'success',
        title: t('instant_visit_modal.visit_started'),
        text: `${t('instant_visit_modal.visit_started')} ${formData.customerName}!`,
        timer: 2000,
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        timerProgressBar: true
      });

      // Navigate after showing the alert
      setTimeout(() => {
        onClose();
        if (onVisitCreated) {
          onVisitCreated();
        }
      }, 2000);

    } catch (error) {
      console.error('❌ Failed to create instant visit:', error);

      // Show error SweetAlert
      await Swal.fire({
        icon: 'error',
        title: t('instant_visit_modal.visit_creation_failed'),
        html: `<div class="text-center">
          <p class="text-gray-600 mb-2">${t('instant_visit_modal.visit_start_error')}</p>
          <p class="text-red-600 font-medium">${error instanceof Error ? error.message : t('instant_visit_modal.unknown_error')}</p>
        </div>`,
        confirmButtonText: t('common.buttons.retry'),
        confirmButtonColor: '#ef4444'
      });
    } finally {
      setIsCreatingVisit(false);
    }
  }, [selectedCustomer, formData, dispatch, onClose, onVisitCreated]);

  // Extract customer phone numbers for NotesModal
  const getCustomerPhoneNumbers = useCallback((customer: Customer | null): string[] => {
    if (!customer) return [];

    // Collect all possible phone fields
    const phoneFields = [
      customer.phone,
      customer.Contacs,
      customer.Phone,
      customer.Kontak,
      formData.phoneNumber,
      formData.whatsappNumber
    ];

    // Filter and deduplicate phone numbers
    const validPhones = phoneFields
      .filter(phone => phone && phone.trim().length > 0 && phone.trim() !== '-' && phone.trim() !== 'N/A')
      .map(phone => phone.trim());

    // Remove duplicates
    const uniquePhones = Array.from(new Set(validPhones));

    console.log('📞 getCustomerPhoneNumbers:', {
      customer: customer.name || customer.NamaPelangganTambak,
      allPhoneFields: phoneFields,
      validPhones,
      uniquePhones,
      result: uniquePhones
    });

    return uniquePhones;
  }, [formData.phoneNumber, formData.whatsappNumber]);

  // Handle NotesModal callbacks - SAVE DATA TO BACKEND
  const handleNotesModalSave = useCallback(async (notes: string, actionType?: string, files?: File[], markedActionType?: 'whatsapp' | 'call' | null, progress?: ProgressStatus) => {
    console.log('📝 NotesModal save from InstantVisit - STARTING SAVE:', {
      notes: notes.length > 0 ? `${notes.substring(0, 50)}...` : 'empty',
      actionType,
      filesCount: files?.length || 0,
      files: files?.map(f => ({ name: f.name, size: f.size, type: f.type })) || [],
      markedActionType,
      progress,
      selectedCustomer: selectedCustomer?.name || 'none'
    });

    if (!selectedCustomer) {
      console.error('❌ No customer selected, cannot save interaction');
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No customer selected. Please select a customer first.',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Only save if there are notes or files (don't save empty interactions)
    if (!notes.trim() && (!files || files.length === 0)) {
      console.log('📝 No notes or files, just closing modal');
      setShowNotesModal(false);
      return;
    }

    try {
      // Step 1: Create Customer Interaction with AUTO-COMPLETE
      const interactionData = {
        No_pelanggan: selectedCustomer.idcust || selectedCustomer.id?.toString() || '',
        keterangan: notes.trim() || 'Interaction from Instant Visit notes',
        interaction_type: markedActionType === 'whatsapp' ? 'WhatsApp' :
                         markedActionType === 'call' ? 'Phone Call' : 'WhatsApp',
        priority: 'medium' as const,
        date_created: getWIBTime(),
        // ✅ FIXED: Only set for Customer_Tambak records, null for Data_Customer
        customer_tambak_id: getCustomerTambakId(selectedCustomer),
        // Progress status from NotesModal
        progress: progress || null,
        // AUTO-COMPLETE: Mark as done immediately after Save & Continue
        done: true,
        completed_date: getWIBTimeForDirectus(),
        status: 'completed' as const
      };

      console.log('📝 Creating interaction from InstantVisit with AUTO-COMPLETE:', interactionData);
      const interactionResult = await dispatch(createCustomerInteraction(interactionData)).unwrap();
      console.log('✅ Interaction created and auto-completed:', {
        id: interactionResult.id,
        done: true,
        completed_date: interactionData.completed_date,
        status: 'completed'
      });

      // Step 2: Upload files if any
      let uploadedFileIds: (string | number)[] = [];
      if (files && files.length > 0) {
        console.log(`📎 Uploading ${files.length} files...`);
        console.log('📎 Files array details:', files.map(f => ({ name: f.name, size: f.size, type: f.type })));

        for (const file of files) {
          try {
            console.log(`📎 Processing file: ${file.name} (${file.size} bytes, ${file.type})`);
            const uploadResult = await directusOperations.uploadAndCreateCustomerInteractionFile(file);
            console.log(`✅ Uploaded file ${file.name}:`, uploadResult.interactionFileRecord.id);
            console.log('📎 Full upload result:', uploadResult);

            const customerInteractionFileId = uploadResult.interactionFileRecord.id;
            if (customerInteractionFileId && (typeof customerInteractionFileId === 'string' || typeof customerInteractionFileId === 'number')) {
              uploadedFileIds.push(customerInteractionFileId);
              console.log(`📎 Added file ID ${customerInteractionFileId} to list`);
            }
          } catch (fileError) {
            console.error(`❌ Failed to upload file ${file.name}:`, fileError);
            console.error('📎 Full file error details:', fileError);
          }
        }

        // Step 3: Link files to interaction if any were uploaded
        if (uploadedFileIds.length > 0 && interactionResult.id) {
          try {
            await dispatch(updateCustomerInteraction({
              id: parseInt(interactionResult.id),
              data: {
                file: uploadedFileIds
              }
            }));
            console.log(`✅ Linked ${uploadedFileIds.length} files to interaction ${interactionResult.id}`);
          } catch (linkError) {
            console.error('❌ Failed to link files to interaction:', linkError);
          }
        }
      }

      // Show success message with completion status
      const successMessage = [
        'Notes saved successfully!',
        files && files.length > 0 ? `${uploadedFileIds.length}/${files.length} files uploaded.` : '',
        markedActionType ? `Interaction type: ${markedActionType === 'whatsapp' ? 'WhatsApp' : 'Phone Call'}` : '',
        progress ? `Progress: ${progress}` : '',
        '✅ Interaction marked as completed!'
      ].filter(Boolean).join(' ');

      await Swal.fire({
        icon: 'success',
        title: 'Saved!',
        text: successMessage,
        confirmButtonText: 'Great!',
        confirmButtonColor: 'hsl(var(--primary))',
        timer: 3000,
        timerProgressBar: true
      });

      // Close modal after successful save
      setShowNotesModal(false);

    } catch (error) {
      console.error('❌ Failed to save interaction from InstantVisit:', error);

      await Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: `Failed to save notes: ${error instanceof Error ? error.message : 'Unknown error'}`,
        confirmButtonText: 'OK',
        confirmButtonColor: 'hsl(var(--destructive))'
      });
    }
  }, [selectedCustomer, dispatch]);

  const handleNotesModalClose = useCallback(() => {
    setShowNotesModal(false);
    setNotesModalInteraction(null);
  }, []);

  const handleNotesModalDirectContact = useCallback(async (actionType: 'whatsapp' | 'call', phoneNumber: string, shouldCloseModal: boolean) => {
    console.log('📞 Direct contact from NotesModal:', { actionType, phoneNumber, shouldCloseModal });

    if (shouldCloseModal) {
      setShowNotesModal(false);
    }

    return true; // Allow the action to proceed
  }, []);

  // Update WIB time every minute for real-time display
  useEffect(() => {
    if (!isOpen) return;

    const updateTime = () => {
      const newWIBTime = getCurrentWIBDateTime();
      setCurrentWIBTime(newWIBTime);

      // Also update form data if user hasn't manually changed the scheduled time
      setFormData(prev => ({
        ...prev,
        date: getCurrentWIBDate(),
        scheduledDateTime: prev.scheduledDateTime === currentWIBTime ? newWIBTime : prev.scheduledDateTime
      }));
    };

    // Update immediately when modal opens
    updateTime();

    // Update every minute (60000ms)
    const interval = setInterval(updateTime, 60000);

    return () => clearInterval(interval);
  }, [isOpen, currentWIBTime]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-black/50 flex items-end z-[60]">
      <div className="bg-card w-full max-h-[95vh] rounded-t-3xl flex flex-col border border-border">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border bg-card shadow-sm flex-shrink-0">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold">
                {formData.customerInitial || 'A'}
              </div>
              <div className="flex-1 relative customer-search-container">
                {/* Customer Search Input */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder={t('instant_visit_modal.search_customer_placeholder')}
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    onFocus={() => {
                      if (customerSearch && searchResults.length > 0) {
                        setShowCustomerDropdown(true);
                      }
                    }}
                    className="w-full text-lg font-bold bg-transparent border border-border rounded px-3 py-1 outline-none placeholder-muted-foreground focus:border-primary"
                  />
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                </div>

                {/* Customer Dropdown */}
                {showCustomerDropdown && (
                  <div className="absolute top-full left-0 right-0 z-50 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto mt-1">
                    {isSearching ? (
                      <div className="p-4 text-center text-muted-foreground">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary mx-auto"></div>
                        <span className="ml-2">{t('instant_visit_modal.searching_customers')}</span>
                      </div>
                    ) : searchResults.length > 0 ? (
                      searchResults.map((customer, index) => (
                        <button
                          key={`customer-${customer.id}-${customer.idcust || customer.No_pelanggan || index}`}
                          onClick={() => handleCustomerSelect(customer)}
                          className="w-full p-3 text-left hover:bg-muted/50 border-b border-border last:border-b-0 transition-colors"
                        >
                          <div className="font-medium text-foreground">
                            {customer.name || customer.NamaPelangganTambak || 'Unknown Customer'}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            ID: {customer.idcust || customer.id}
                          </div>
                          {customer.address && (
                            <div className="text-xs text-muted-foreground truncate">
                              {customer.address}
                            </div>
                          )}
                        </button>
                      ))
                    ) : customerSearch.length >= 2 ? (
                      <div className="p-4 text-center text-muted-foreground">
                        {t('instant_visit_modal.no_customers_found', { search: customerSearch })}
                      </div>
                    ) : (
                      <div className="p-4 text-center text-muted-foreground">
                        {t('instant_visit_modal.type_min_chars')}
                      </div>
                    )}
                  </div>
                )}

                <input
                  type="text"
                  placeholder={t('instant_visit_modal.customer_id_placeholder')}
                  value={formData.customerId}
                  onChange={(e) => setFormData(prev => ({ ...prev, customerId: e.target.value }))}
                  className="w-full text-sm text-muted-foreground bg-transparent border-none outline-none placeholder-muted-foreground mt-1"
                  readOnly
                />
              </div>
            </div>
            <select
              value={formData.status}
              onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
              className="px-3 py-1 text-sm rounded-full font-medium bg-primary/10 text-primary border border-primary/20"
            >
              <option value="">{t('instant_visit_modal.select_status')}</option>
              <option value="scheduled">{t('instant_visit_modal.status_scheduled')}</option>
              <option value="pending">{t('instant_visit_modal.status_pending')}</option>
              <option value="completed">{t('instant_visit_modal.status_completed')}</option>
            </select>
          </div>
          <div className="flex flex-col items-end space-y-2">
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted/50 rounded-full transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="text-right space-y-1">
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                className="text-right text-muted-foreground text-xs bg-transparent border border-border rounded px-2 py-1 outline-none"
              />
              <div className="text-xs text-muted-foreground">
                WIB
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 min-h-0 max-h-full overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Interaction Details */}
            <div className="space-y-4">
              {/* Interaction Type and DateTime */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-muted-foreground">{t('instant_visit_modal.type_label')}</label>
                  <div className="flex items-center space-x-2">
                    <Users className="w-5 h-5 text-muted-foreground" />
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                      className="flex-1 bg-transparent border border-border rounded px-3 py-1 text-sm"
                    >
                      <option value="">{t('instant_visit_modal.select_type')}</option>
                      <option value="visit">{t('instant_visit_modal.type_visit')}</option>
                      <option value="call">{t('instant_visit_modal.type_call')}</option>
                      <option value="whatsapp">{t('instant_visit_modal.type_whatsapp')}</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-muted-foreground">{t('instant_visit_modal.scheduled_wib')}</label>
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <input
                      type="datetime-local"
                      value={formData.scheduledDateTime}
                      onChange={(e) => setFormData(prev => ({ ...prev, scheduledDateTime: e.target.value }))}
                      className="flex-1 bg-transparent border border-border rounded px-3 py-1 text-sm"
                      title="Waktu Indonesia Barat (WIB)"
                    />
                  </div>
                  <div className="text-xs text-muted-foreground pl-6">
                    {t('instant_visit_modal.realtime_wib')}
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-muted-foreground">{t('instant_visit_modal.notes_details')}</label>
                </div>

                <div className="bg-muted/50 rounded-lg p-4">
                  <textarea
                    placeholder={t('instant_visit_modal.add_notes_placeholder')}
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full bg-transparent border-none outline-none resize-none text-muted-foreground text-sm"
                    rows={4}
                  />
                </div>
              </div>
            </div>

            {/* Quick Contact Actions */}
            <div className="bg-muted/50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-muted-foreground">{t('instant_visit_modal.quick_contact')}</h3>
              </div>

              {/* Phone Numbers Info */}
              <div className="mb-3 p-2 bg-card rounded-lg border border-border">
                <div className="text-xs text-muted-foreground mb-1">
                  {t('instant_visit_modal.phone_numbers')}
                  {selectedCustomer && (selectedCustomer.phone || selectedCustomer.Contacs || selectedCustomer.Phone || selectedCustomer.Kontak) && (
                    <span className="ml-1 text-green-600">• {t('instant_visit_modal.auto_populated')}</span>
                  )}
                  {selectedCustomer && !(selectedCustomer.phone || selectedCustomer.Contacs || selectedCustomer.Phone || selectedCustomer.Kontak) && (
                    <span className="ml-1 text-amber-600">• {t('instant_visit_modal.no_phone_number')}</span>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Phone className="w-4 h-4 text-primary" />
                    <input
                      type="tel"
                      placeholder={selectedCustomer && !(selectedCustomer.phone || selectedCustomer.Contacs || selectedCustomer.Phone || selectedCustomer.Kontak)
                        ? t('instant_visit_modal.no_phone_available')
                        : t('instant_visit_modal.phone_placeholder')
                      }
                      value={formData.phoneNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                      className="flex-1 bg-transparent border border-border rounded px-2 py-1 text-sm"
                    />
                    <span className="text-xs text-muted-foreground">(Phone)</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <MessageCircle className="w-4 h-4 text-green-500" />
                    <input
                      type="tel"
                      placeholder={selectedCustomer && !(selectedCustomer.phone || selectedCustomer.Contacs || selectedCustomer.Phone || selectedCustomer.Kontak)
                        ? t('instant_visit_modal.no_whatsapp_available')
                        : t('instant_visit_modal.whatsapp_placeholder')
                      }
                      value={formData.whatsappNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, whatsappNumber: e.target.value }))}
                      className="flex-1 bg-transparent border border-border rounded px-2 py-1 text-sm"
                    />
                    <span className="text-xs text-muted-foreground">(WhatsApp)</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleWhatsAppCallClick}
                  disabled={!selectedCustomer}
                  className={`flex flex-col items-center space-y-2 p-3 rounded-lg transition-colors ${
                    !selectedCustomer
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-green-100 to-blue-100 text-gray-800 hover:from-green-200 hover:to-blue-200'
                  }`}
                  title={!selectedCustomer ? t('instant_visit_modal.please_select_customer_first') : t('instant_visit_modal.open_notes_contact')}
                >
                  <div className="flex items-center space-x-1">
                    <MessageCircle className={`w-5 h-5 ${!selectedCustomer ? 'text-gray-400' : 'text-green-600'}`} />
                    <span className="text-xs">/</span>
                    <Phone className={`w-5 h-5 ${!selectedCustomer ? 'text-gray-400' : 'text-primary'}`} />
                  </div>
                  <span className="text-xs font-medium">{t('instant_visit_modal.whatsapp_call_button')}</span>
                  {!selectedCustomer && (
                    <span className="text-xs text-gray-400">{t('instant_visit_modal.select_customer')}</span>
                  )}
                </button>

                <button
                  onClick={handleVisitClick}
                  disabled={!selectedCustomer || !formData.type || isCreatingVisit}
                  className={`flex flex-col items-center space-y-2 p-3 rounded-lg transition-colors ${
                    !selectedCustomer || !formData.type || isCreatingVisit
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                  }`}
                >
                  {isCreatingVisit ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                  ) : (
                    <MapPin className="w-6 h-6" />
                  )}
                  <span className="text-xs font-medium">
                    {isCreatingVisit ? t('instant_visit_modal.visit_starting') : t('instant_visit_modal.visit_button')}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* NotesModal for WhatsApp/Call */}
      {showNotesModal && notesModalInteraction && selectedCustomer && (
        <NotesModal
          isOpen={showNotesModal}
          onClose={handleNotesModalClose}
          onSave={handleNotesModalSave}
          title={t('instant_visit_modal.add_notes_contact')}
          placeholder={t('instant_visit_modal.add_notes_placeholder')}
          customerName={notesModalInteraction.customer_name}
          customerPhoneNumbers={getCustomerPhoneNumbers(selectedCustomer)}
          onDirectContact={handleNotesModalDirectContact}
          enableFileUpload={true}
          enableProgressSelection={true}
          initialProgress={notesModalInteraction?.Progress || null}
          showActionSelection={false}
        />
      )}
    </div>
  );
};